/**
 * Shared visual primitives for the admin panel.
 *
 * Kept intentionally small — Button, Pill, Banner, Card, Spinner, AuditChips.
 * Heavier components (DataTable, FormField, ModalShell) live in their own
 * files. Tokens come from globals.css; no inline color literals here.
 */

import type { CSSProperties, ReactNode, MouseEvent } from 'react';

// Redesign primitives (0.53.0), shared with the risk and sysops panels: status chips carry an icon
// and words (never colour alone); the page header is H1 + one line + the primary action; empty
// states say what to do next; filter chips are a single-choice group.
export { StatusChip, PageHeader, EmptyState, FilterChips, type StatusTone, type FilterChipOption } from '@retail-ops/panel-shell';

// ─── Button ──────────────────────────────────────────────────────────────

export interface ButtonProps {
  variant?: 'primary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
  style?: CSSProperties;
}

export function Button({
  variant = 'primary',
  size = 'md',
  disabled,
  type = 'button',
  onClick,
  children,
  style,
}: ButtonProps): JSX.Element {
  const cls = `btn btn--${variant}${size === 'sm' ? ' btn--sm' : ''}`;
  return (
    <button type={type} className={cls} disabled={disabled} onClick={onClick} style={style}>
      {children}
    </button>
  );
}

// ─── Pill ────────────────────────────────────────────────────────────────

export type PillTone = 'ok' | 'warn' | 'err' | 'info' | 'muted';

export function Pill({ tone, children }: { tone: PillTone; children: ReactNode }): JSX.Element {
  return <span className={`pill pill--${tone}`}>{children}</span>;
}

// ─── Banner ──────────────────────────────────────────────────────────────

export type BannerTone = 'info' | 'warn' | 'err';

export function Banner({
  tone,
  children,
}: {
  tone: BannerTone;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={`banner banner--${tone}`} role="status">
      {children}
    </div>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────

export function Card({
  title,
  actions,
  children,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-header">
          {title && <h3>{title}</h3>}
          {actions && <div className="card-header__actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

// ─── Loading / Empty ─────────────────────────────────────────────────────

export function Spinner({ label = 'Loading…' }: { label?: string }): JSX.Element {
  return <div className="loading-inline">{label}</div>;
}

export function Empty({ children }: { children: ReactNode }): JSX.Element {
  return <div className="empty">{children}</div>;
}

// ─── AuditChips — created/updated metadata strip ─────────────────────────

export interface AuditChipsProps {
  createdAt?: string | null;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

function formatIso(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function AuditChips({
  createdAt,
  createdBy,
  updatedAt,
  updatedBy,
}: AuditChipsProps): JSX.Element {
  return (
    <div className="audit-chips">
      {(createdAt || createdBy) && (
        <span className="audit-chips__chip">
          <strong>Created:</strong> {formatIso(createdAt)}
          {createdBy ? ` by ${createdBy}` : ''}
        </span>
      )}
      {(updatedAt || updatedBy) && (
        <span className="audit-chips__chip">
          <strong>Updated:</strong> {formatIso(updatedAt)}
          {updatedBy ? ` by ${updatedBy}` : ''}
        </span>
      )}
    </div>
  );
}
